import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle, Shield, Clock, ChevronDown, AlertTriangle, FileText, MapPin } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CTASection } from "@/components/site/CTASection";
import collectionIcon from "@/assets/icons/collection-red.webp";
import calendrierIcon from "@/assets/icons/calendrier-blue.webp";
import { SEOHead } from "@/components/SEOHead";
import { BreadcrumbSchema, FAQSchema } from "@/components/SchemaOrg";
import { DetailedDefects } from "@/components/site/DetailedDefects";
import ferrariFacade from "@/assets/centre/ferrari-308-facade.webp";
import rollsRoyceFacade from "@/assets/centre/rolls-royce-facade.webp";
import benelli750 from "@/assets/centre/benelli-750-collection.webp";
import miniRedbull from "@/assets/centre/mini-redbull-autosur.webp";

const collectionTypes = [
  "Véhicules de plus de 30 ans",
  "Voitures de collection avec CG collection",
  "Youngtimers (20-30 ans)",
  "Véhicules historiques",
  "Véhicules atypiques",
  "Cabriolets anciens",
  "Véhicules d'exception",
];

const commonDefectsCollection = [
  { category: "Corrosion", details: "Rouille structurelle, perforations bas de caisse, passages de roue attaqués, plancher fragilisé" },
  { category: "Éclairage", details: "Optiques jaunies, connectique oxydée, phares mal réglés (réglage d'époque), masse défectueuse" },
  { category: "Freinage", details: "Flexibles poreux, maître-cylindre fuyard, garnitures durcies, tambours ovalisés" },
  { category: "Direction", details: "Jeu dans le boîtier de direction, rotules usées, biellettes avec jeu, crémaillère fuiteuse" },
  { category: "Pollution", details: "Émissions excessives (carburateur mal réglé), échappement percé, catalyseur absent (pré-1993)" },
  { category: "Étanchéité", details: "Fuites moteur/boîte (joints vieillissants), circuit de refroidissement, liquide de frein" },
];

const detailedDefectsCollection = [
  {
    system: "Corrosion & structure",
    percent: 35,
    color: "bg-red-500",
    items: [
      { name: "Rouille structurelle", description: "attaque profonde du métal" },
      { name: "Perforations bas de caisse", description: "trous visibles, intégrité compromise" },
      { name: "Passages de roue attaqués", description: "projections d'eau et sel" },
      { name: "Plancher fragilisé", description: "risque de perforation" },
    ],
  },
  {
    system: "Éclairage d'époque",
    percent: 28,
    color: "bg-yellow-500",
    items: [
      { name: "Optiques jaunies", description: "intensité lumineuse insuffisante" },
      { name: "Connectique oxydée", description: "contacts intermittents" },
      { name: "Masse défectueuse", description: "mise à la terre dégradée" },
      { name: "Réglage d'époque", description: "hauteur de faisceau incorrecte" },
    ],
  },
  {
    system: "Freinage ancien",
    percent: 22,
    color: "bg-orange-500",
    items: [
      { name: "Flexibles poreux", description: "durcis avec l'âge, micro-fissures" },
      { name: "Maître-cylindre fuyard", description: "joints internes défaillants" },
      { name: "Garnitures durcies", description: "perte d'efficacité" },
      { name: "Tambours ovalisés", description: "freinage irrégulier" },
    ],
  },
  {
    system: "Direction & châssis",
    percent: 18,
    color: "bg-blue-600",
    items: [
      { name: "Jeu boîtier de direction", description: "flottement au volant" },
      { name: "Rotules usées", description: "jeu dangereux" },
      { name: "Crémaillère fuiteuse", description: "perte d'assistance" },
    ],
  },
  {
    system: "Pollution (carburateur)",
    percent: 15,
    color: "bg-green-600",
    items: [
      { name: "Carburateur mal réglé", description: "mélange trop riche" },
      { name: "Échappement percé", description: "émissions non mesurables" },
      { name: "Pas de catalyseur", description: "véhicules pré-1993" },
    ],
  },
  {
    system: "Étanchéité & fluides",
    percent: 12,
    color: "bg-purple-500",
    items: [
      { name: "Fuites moteur", description: "joints d'époque vieillissants" },
      { name: "Fuites boîte de vitesses", description: "joints spi usés" },
      { name: "Circuit de refroidissement", description: "durites craquelées" },
    ],
  },
];

const CollectionPage = () => {
  return (
    <SiteLayout>
      <SEOHead
        title="CT Véhicule de Collection"
        description="Contrôle technique véhicule de collection à Montreuil : expertise anciennes et prestige. Contrôle adapté, équipe passionnée. RDV en ligne 24/7."
        path="/vehicules/collection"
      />
      <BreadcrumbSchema items={[{ name: "Accueil", href: "/" }, { name: "Véhicules", href: "/prestations" }, { name: "Collection", href: "/vehicules/collection" }]} />
      <FAQSchema items={[
        { question: "Fréquence du CT collection ?", answer: "Tous les 5 ans avec carte grise collection (2 ans sinon)." },
        { question: "Combien coûte le CT collection ?", answer: "À partir de 73€ en ligne (80€ standard)." },
        { question: "Faut-il une carte grise collection ?", answer: "Non obligatoire pour le CT, mais permet le régime 5 ans. Véhicule 30+ ans, plus produit." },
        { question: "Contrôlez-vous les youngtimers ?", answer: "Oui, véhicules 20-30 ans. Régime standard sauf carte grise collection." },
        { question: "Le contrôle est-il différent ?", answer: "Mêmes points réglementaires, mais attention aux spécificités et authenticité." },
        { question: "Combien de temps dure le CT collection ?", answer: "45-60 minutes. Attention particulière aux spécificités." },
        { question: "Quels documents apporter ?", answer: "Carte grise. Carte grise collection si applicable." },
        { question: "Les véhicules d'avant 1960 sont-ils acceptés ?", answer: "Oui, nous contrôlons tous les véhicules anciens, même les modèles d'avant 1960. Nos contrôleurs connaissent les spécificités de ces véhicules." },
        { question: "Quels défauts courants trouve-t-on sur les véhicules anciens ?", answer: "Corrosion, éclairage (optiques jaunies, connectique oxydée), freinage (flexibles poreux), direction (jeu) et pollution (carburateur mal réglé)." },
      ]} />
      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${rollsRoyceFacade})` }}
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-body mb-6 border border-white/20">
              <img src={collectionIcon} alt="Collection" className="w-6 h-6 brightness-0 invert" />
              Collection & Anciennes
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Contrôle Technique Véhicule de Collection à Montreuil-sous-Bois
            </h1>
            <p className="font-body text-white/80 text-lg mb-4">
              Un contrôle technique adapté aux véhicules de plus de 30 ans, aux youngtimers 
              et aux voitures atypiques, dans le respect de leur authenticité.
            </p>
            <p className="font-body text-white/60 text-sm">
              CT voiture ancienne • Visite technique collection • Inspection véhicule historique
            </p>
          </motion.div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-6">
                Le contrôle technique des véhicules de collection et anciens
              </h2>
              <div className="space-y-4 font-body text-muted-foreground">
                <p>
                  Les véhicules de collection bénéficient d'un <strong className="text-autosur-navy">régime 
                  spécifique</strong> pour le contrôle technique. Avec une <strong className="text-autosur-navy">carte grise collection</strong>, la visite technique 
                  est obligatoire tous les 5 ans seulement (au lieu de 2 ans pour les véhicules standard).
                </p>
                <p>
                  Chez Autosur Montreuil-sous-Bois, nous comprenons la passion qui anime les propriétaires 
                  de <strong className="text-autosur-navy">voitures anciennes et véhicules historiques</strong>. Notre équipe est formée aux 
                  spécificités de ces véhicules d'exception : mécanique d'époque, matériaux spécifiques, 
                  normes de construction différentes.
                </p>
                <p>
                  Nous contrôlons également les <strong className="text-autosur-navy">youngtimers</strong> (véhicules de 20 à 30 ans), les véhicules 
                  atypiques, les modèles rares et les automobiles de prestige qui nécessitent une attention particulière 
                  et un diagnostic automobile adapté.
                </p>
              </div>

              {/* Info box */}
              <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
                <h3 className="font-heading font-bold text-autosur-navy mb-3">
                  Carte grise collection : conditions d'obtention
                </h3>
                <p className="font-body text-sm text-autosur-navy mb-3">
                  Pour bénéficier du régime spécifique (CT tous les 5 ans), votre véhicule doit remplir ces critères :
                </p>
                <ul className="space-y-2 font-body text-sm text-autosur-navy">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Avoir plus de 30 ans d'âge
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Ne plus être produit par le constructeur
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Être dans son état d'origine ou restauré fidèlement
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Avoir obtenu l'attestation de la FFVE (Fédération Française des Véhicules d'Époque)
                  </li>
                </ul>
              </div>

              {/* Price box */}
              <div className="mt-6 bg-autosur-blue/5 rounded-2xl p-6 border border-autosur-blue/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading font-bold text-autosur-navy text-lg">Véhicule de collection</p>
                    <p className="font-body text-muted-foreground text-sm">Carte grise collection</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-3xl font-bold text-autosur-red">73€</p>
                    <p className="font-body text-muted-foreground text-xs">
                      <span className="line-through">80€</span> À partir de (*)
                    </p>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="mt-4 block w-full py-3 bg-autosur-red text-white font-heading font-semibold text-center rounded-full hover:bg-autosur-red-light transition-colors"
                >
                  Réserver en ligne – Économisez 7€
                </Link>
                <p className="font-body text-muted-foreground text-[10px] mt-2 text-center">(*) Offre non cumulable</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              {/* Types de véhicules */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-heading font-bold text-autosur-navy mb-4">Véhicules de collection acceptés</h3>
                <ul className="grid grid-cols-1 gap-3">
                  {collectionTypes.map((type) => (
                    <li key={type} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="font-body text-autosur-navy">{type}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Images */}
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={rollsRoyceFacade}
                  alt="Rolls-Royce Silver Shadow devant le centre Autosur Montreuil-sous-Bois"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={ferrariFacade}
                  alt="Ferrari 308 GTS devant le centre de contrôle technique Autosur"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Défauts courants collection */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Défauts courants sur les véhicules anciens et de collection
            </h2>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              Les véhicules de collection présentent des particularités liées à leur âge et leur conception d'époque. 
              Voici les anomalies les plus fréquentes lors de l'inspection.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {commonDefectsCollection.map((defect, index) => (
              <motion.div
                key={defect.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
              >
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <h3 className="font-heading font-bold text-autosur-navy">{defect.category}</h3>
                </div>
                <p className="font-body text-muted-foreground text-sm">{defect.details}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Defects Collection */}
      <DetailedDefects
        title="Défauts détectés par système sur les véhicules anciens"
        subtitle="Les véhicules de collection présentent des défauts spécifiques liés à leur âge et leur conception d'époque. Voici le détail par système avec le taux de détection."
        defects={detailedDefectsCollection}
      />

      {/* Préparation collection */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
                Préparer votre véhicule ancien avant le contrôle
              </h2>
              <p className="font-body text-muted-foreground mb-6">
                Un véhicule de collection bien entretenu a toutes les chances de passer le CT sans difficulté. 
                Voici les points essentiels à vérifier.
              </p>
              <ul className="space-y-3">
                {[
                  "Vérifier l'ensemble de l'éclairage (connectique souvent oxydée sur les anciens modèles)",
                  "Contrôler l'état des flexibles de frein (durcissement avec l'âge = danger)",
                  "Inspecter visuellement la corrosion (bas de caisse, passages de roue, plancher)",
                  "Vérifier l'étanchéité des circuits (huile, eau, frein) – fuites fréquentes sur les anciens",
                  "S'assurer du bon réglage du carburateur (pollution excessive = contre-visite)",
                  "Contrôler les pneus (craquelures dues au vieillissement même sans kilométrage)",
                  "Vérifier le jeu dans la direction (rotules, crémaillère ou boîtier)",
                  "Apporter la carte grise et la carte grise collection si applicable",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="font-body text-autosur-navy text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-4"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={benelli750}
                  alt="Benelli 750 de collection au centre Autosur Montreuil-sous-Bois"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={miniRedbull}
                  alt="Mini Red Bull de collection au centre Autosur"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Pourquoi choisir Autosur pour votre CT collection ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { iconSrc: calendrierIcon, title: "RDV en ligne", desc: "Réservez 24h/24" },
              { icon: Shield, title: "Centre agréé", desc: "Homologation État" },
              { icon: Clock, title: "Temps dédié", desc: "45-60 minutes" },
              { icon: CheckCircle, title: "Résultat immédiat", desc: "PV instantané" },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-default"
              >
                <div className="w-12 h-12 mx-auto mb-4 bg-autosur-blue/10 rounded-xl flex items-center justify-center">
                  {item.iconSrc ? (
                    <img src={item.iconSrc} alt={item.title} className="w-7 h-7 object-contain" />
                  ) : item.icon ? (
                    <item.icon className="w-6 h-6 text-autosur-blue" />
                  ) : null}
                </div>
                <h3 className="font-heading font-bold text-autosur-navy mb-2">{item.title}</h3>
                <p className="font-body text-muted-foreground text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-6 text-center">
            Découvrez aussi nos autres contrôles techniques
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8">
            <Link to="/vehicules/voitures" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">CT voiture</h3>
              <p className="font-body text-muted-foreground text-xs">Berlines, SUV, citadines • Dès 73€</p>
            </Link>
            <Link to="/vehicules/motos" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">CT moto</h3>
              <p className="font-body text-muted-foreground text-xs">Obligatoire depuis 2024 • Dès 70€</p>
            </Link>
            <Link to="/vehicules/utilitaires" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">CT utilitaire</h3>
              <p className="font-body text-muted-foreground text-xs">Fourgons, camping-cars • Dès 79€</p>
            </Link>
            <Link to="/prestations" className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-autosur-blue/30 transition-all group">
              <h3 className="font-heading font-bold text-autosur-navy text-sm mb-1 group-hover:text-autosur-blue transition-colors">Tous nos tarifs</h3>
              <p className="font-body text-muted-foreground text-xs">Grille tarifaire complète</p>
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl mx-auto">
            <Link to="/blog/premier-controle-technique-4-ans-guide" className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-100 rounded-xl font-body text-autosur-navy text-sm hover:border-autosur-blue/30 hover:bg-autosur-blue/5 transition-all">
              <FileText className="w-4 h-4 text-autosur-blue flex-shrink-0" />
              Premier CT : guide complet
            </Link>
            <Link to="/le-centre" className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-100 rounded-xl font-body text-autosur-navy text-sm hover:border-autosur-blue/30 hover:bg-autosur-blue/5 transition-all">
              <FileText className="w-4 h-4 text-autosur-blue flex-shrink-0" />
              Découvrir notre centre
            </Link>
            <Link to="/faq" className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-100 rounded-xl font-body text-autosur-navy text-sm hover:border-autosur-blue/30 hover:bg-autosur-blue/5 transition-all">
              <FileText className="w-4 h-4 text-autosur-blue flex-shrink-0" />
              Toutes nos FAQ
            </Link>
          </div>

          <div className="mt-10 text-center">
            <h3 className="font-heading font-bold text-autosur-navy mb-4">CT collection près de chez vous</h3>
            <div className="flex flex-wrap gap-2 justify-center">
              {["controle-technique-vincennes", "controle-technique-saint-mande", "controle-technique-fontenay-sous-bois", "controle-technique-bagnolet", "controle-technique-les-lilas"].map((slug) => (
                <Link
                  key={slug}
                  to={`/${slug}`}
                  className="inline-flex items-center gap-1 px-4 py-2 bg-white border border-gray-200 rounded-full font-body text-autosur-navy text-xs hover:border-autosur-blue/50 hover:bg-autosur-blue/5 transition-all"
                >
                  <MapPin className="w-3 h-3 text-autosur-blue" />
                  {slug.replace("controle-technique-", "").replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-autosur-navy mb-8 text-center">
              Questions fréquentes sur le CT collection
            </h2>
            <div className="space-y-4">
              {[
                { q: "Quelle est la fréquence du CT pour un véhicule de collection ?", a: "Les véhicules avec carte grise collection bénéficient d'un régime spécifique : le contrôle technique est obligatoire tous les 5 ans (au lieu de 2 ans pour les véhicules standard). Sans carte grise collection, le régime est identique aux véhicules classiques." },
                { q: "Combien coûte le CT collection ?", a: "Le contrôle technique véhicule de collection est à partir de 73€ en réservant en ligne (80€ standard). Économisez 7€ en réservant sur notre site. Même tarif que le CT voiture standard." },
                { q: "Faut-il une carte grise collection ?", a: "Pas obligatoirement pour passer le CT. Cependant, la carte grise collection vous permet de bénéficier du CT tous les 5 ans. Le véhicule doit avoir plus de 30 ans, ne plus être produit, et être dans son état d'origine ou restauré fidèlement." },
                { q: "Contrôlez-vous les youngtimers (20-30 ans) ?", a: "Oui ! Nous contrôlons les youngtimers, les véhicules atypiques et les modèles rares. Ces véhicules suivent le régime standard (CT tous les 2 ans) sauf s'ils ont obtenu la carte grise collection." },
                { q: "Le contrôle est-il différent pour les véhicules anciens ?", a: "Le contrôle porte sur les mêmes points réglementaires (133 points), mais nos contrôleurs sont formés aux spécificités des véhicules anciens : mécanique d'époque, matériaux spécifiques, normes de fabrication différentes. Ils tiennent compte de l'authenticité du véhicule." },
                { q: "Combien de temps dure le CT collection ?", a: "Un contrôle technique collection prend environ 45 à 60 minutes, un peu plus long que pour un véhicule standard car nous accordons une attention particulière aux spécificités du véhicule et à son état général." },
                { q: "Quels documents apporter ?", a: "Apportez votre carte grise (certificat d'immatriculation). Si vous avez la carte grise collection, apportez-la pour bénéficier du régime spécifique (CT tous les 5 ans). Pour une contre-visite, l'ancien procès-verbal." },
                { q: "Les véhicules d'avant 1960 sont-ils acceptés ?", a: "Oui, nous accueillons tous les véhicules anciens, y compris les modèles d'avant 1960. Nos contrôleurs connaissent les particularités mécaniques de ces véhicules d'exception et adaptent leur approche en conséquence." },
                { q: "Quels défauts courants trouve-t-on sur les collections ?", a: "Les défauts les plus fréquents sur les véhicules anciens sont : la corrosion structurelle, l'éclairage défaillant (connectique oxydée), les freins (flexibles poreux), la direction avec jeu, les émissions excessives (carburateur mal réglé) et les fuites de liquides (joints vieillissants)." },
              ].map((faq, index) => (
                <details key={index} className="bg-white rounded-xl border border-gray-100 shadow-sm group">
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-heading font-semibold text-autosur-navy text-sm hover:text-autosur-blue transition-colors">
                    {faq.q}
                    <ChevronDown className="w-5 h-5 text-muted-foreground group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
                  </summary>
                  <div className="px-5 pb-5 pt-0">
                    <p className="font-body text-muted-foreground text-sm">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <CTASection 
        title="Propriétaire d'un véhicule de collection ?"
        subtitle="Confiez votre véhicule d'exception à des experts qui le comprennent."
      />
    </SiteLayout>
  );
};

export default CollectionPage;
